import angular from 'angular'
import DOMPurify from 'dompurify'

/**
 * Controller class for confirm modal.
 *
 * @class ConfirmModalController
 */
export default class ConfirmModalController {
  constructor($q, $log, $sce, close, options) {
    'ngInject'
    this._$q = $q
    this._$log = $log
    this._close = close
    const sanitizedContent = DOMPurify.sanitize(options.content)
    this.content = $sce.trustAsHtml(sanitizedContent)

    /**
     * Default configurations of the confirm and cancel buttons.
     *
     * @property BUTTON_DEFAULTS
     * @type {Object}
     */
    this.BUTTON_DEFAULTS = {
      cancel: {
        label: 'Cancel',
        category: 'supplementary',
        isVisible: true,
        isFocused: false,
        resolve: () => $q.reject(),
      },
      confirm: {
        label: 'Confirm',
        category: 'do',
        isVisible: true,
        isFocused: true,
        resolve: () => $q.resolve(),
      },
    }

    /**
     * Confirm Modal options, such as modal size.
     *
     * @property options
     * @type {Object}
     */
    this.options = options

    /**
     * If the modal can be dismissed. Defaults to true if value not provided.
     *
     * @property dismissible
     * @type {Boolean}
     */
    this.dismissible = angular.isUndefined(options.dismissible)
      ? true
      : options.dismissible

    /**
     * Flag to highlight if a confirm button `resolve` has been triggered.
     *
     * @property isConfirming
     * @type {Boolean}
     */
    this.isConfirming = false

    /**
     * Wrap the modal close handler so we can reject. Handles a modal close triggered by escape key, modal overlay
     * click or the clicking of the close button.
     *
     * @property close
     * @type {Function}
     */
    this.close = () => close($q.reject())

    this._buildConfirmButtons()
  }

  /**
   * Click handler for each provided confirm buttons. Resolves the provided `resolve`, keeping the modal open if any
   * fail. Uses the modal's default `close` method when successful, so as to not reject the `Confirm` promise.
   *
   * @method triggerButtonResolve
   *
   * @param  {Object} button
   *         The button instance
   */
  triggerButtonResolve(button) {
    const resetDismissibleOptionOnError = this.dismissible

    this.isConfirming = true
    this.dismissible = false

    this._resolve(button.resolve)
      .then(result => this._close(result))
      .catch(error => {
        this.isConfirming = false
        this.dismissible = resetDismissibleOptionOnError

        if (error) {
          this._$log.error(error)
        }
      })
  }

  /**
   * Determine if at least one of the configured buttons is visible.
   *
   * @method hasVisibleButtons
   */
  hasVisibleButtons() {
    return (
      this.cancelButton.isVisible ||
      this.confirmButtons.some(button => button.isVisible)
    )
  }

  /**
   * Resolves a set of promises or functions returning promises.
   *
   * It is a simpler, smaller version of `ui.router#$resolve` service.
   *
   * @private
   * @method _resolve
   *
   * @param {Object} options
   *        Either a promise, of a map of promises or function that return a promise.
   *
   * @return {Object}
   *         An object containing the result of each promise.
   */
  _resolve(options) {
    // If options is a thenable, then return directly
    if (options.then) {
      return options
    }

    // If options is a function, immediately invoke and resolve
    if (angular.isFunction(options)) {
      return this._$q.resolve(options())
    }

    const promises = {}
    // If the items in the options are function, call them
    angular.forEach(options, (value, name) => {
      promises[name] = angular.isFunction(value) ? value() : value
    })

    return this._$q.all(promises)
  }

  /**
   * Get the configuration for each button.
   *
   * @private
   * @method _getButtonConfig
   *
   * @param {String} buttonType
   *        The type of button, 'cancel' or 'confirm'
   *
   * @param {String|Object|Array} buttonConfig
   *        The provided button configuration
   */
  _getButtonConfig(buttonType, buttonConfig) {
    buttonConfig = angular.isString(buttonConfig)
      ? { label: buttonConfig }
      : buttonConfig

    // @warning passing `resolve` as part of `options` is deprecated. We assign it to the confirm button here to
    // ensure backward compatiblilty.
    if (buttonType === 'confirm' && this.options.resolve) {
      buttonConfig.resolve = this.options.resolve
    }

    const button = angular.merge(
      {},
      this.BUTTON_DEFAULTS[buttonType],
      buttonConfig
    )

    return button
  }

  /**
   * Construct the modal buttons.
   *
   * @private
   * @method _buildConfirmButtons
   */
  _buildConfirmButtons() {
    const confirmButtons = angular.isArray(this.options.confirm)
      ? this.options.confirm
      : [this.options.confirm]

    this.confirmButtons = confirmButtons.map(buttonConfig =>
      this._getButtonConfig('confirm', buttonConfig)
    )
    this.cancelButton = this._getButtonConfig('cancel', this.options.cancel)
  }
}
